import { defineStore } from 'pinia';
import { store } from '@/common/root';
import {
  ABGW_DeploymentInfo,
  ABGW_DeploymentNodes,
  ABGW_HostInfo,
  ABGW_Process,
  ABGW_VolumeParamsV2,
  BackendTask,
  DeployStandaloneABGW_RequestBody,
  ID,
  ScheduledTask,
  SystemdStatus,
  TaskState,
  VolumeParamsWithReencodingV2,
  ABGW_ThrottlingLimits,
} from '@schema';
import waitTask from '@/common/utils/waitTask';
import i18n from '@/common/i18n';
import waitABGWTask from '@/common/utils/waitABGWTask';
import {
  ABGW_DEPLOYMENT_MODES,
  ABGW_STORAGE_TYPE_CLOUD,
  ABGW_STORAGE_TYPE_LOCAL,
  ABGW_STORAGE_TYPE_NFS,
  ABGW_STORAGE_TYPE_STOREONCE,
  MIN_THROTTLING_LIMITS,
  SOFT_THRESHOLD,
  S3_THRESHOLD
} from '@/common/constants/abgw.constants';
import { TaskName } from '@/common/constants/taskNames.constants';
import api from '@/common/utils/api';
import getClusterId from '@/common/store/pinia/storageCluster/getClusterId';

let fetchTimerId;

export const useAbgwStore = defineStore('abgw', {
  state: () => ({
    config: null as ABGW_DeploymentInfo|null|false,
    status: null as ABGW_DeploymentInfo.status|null,
    loading: false,
    processRetryInProgress: false,
    volumeParams: null as VolumeParamsWithReencodingV2|null,
    loadingVolumeParams: false,
    throttlingLimits: {} as ABGW_ThrottlingLimits
  }),
  getters: {
    deploymentMode: state => {
      if (!state.config) {
        return null;
      }

      if (state.config.deployment_mode.some(deploymentMode => deploymentMode === ABGW_DEPLOYMENT_MODES.STANDALONE)
        && state.config.deployment_mode.some(deploymentMode => deploymentMode === ABGW_DEPLOYMENT_MODES.UPSTREAM)) {
        return ABGW_DEPLOYMENT_MODES.STANDALONE_AND_UPSTREAM;
      }

      return state.config.deployment_mode[0];
    },
    isInstallationFailed: state => state.status === ABGW_DeploymentInfo.status.DEPLOYING_FAILED,
    isDeploying: (state) => state.status === ABGW_DeploymentInfo.status.DEPLOYING,
    isLoading(state): boolean {
      return state.loading || this.isDeploying;
    },
    isUninstalled: state => state.config === false,
    isInstalled: state => !!state.config,
    isVolumeParamsChanging: state => !!state.volumeParams?.initial,
    isGeoReplicatingConfigured: state => state.config ? state.config.geo_replication : false,
    nodes: () => store.getters['locations/allNodes']
      .filter(node => node.services.find(service => service.key === 'abgw')),
    storageGroup: state => {
      if (state.config) {
        if (state.config.storage_type === ABGW_STORAGE_TYPE_LOCAL) {
          return ABGW_STORAGE_TYPE_LOCAL;
        }
        if (state.config.storage_type === ABGW_STORAGE_TYPE_NFS) {
          return ABGW_STORAGE_TYPE_NFS;
        }
        if (state.config.storage_type === ABGW_STORAGE_TYPE_STOREONCE) {
          return ABGW_STORAGE_TYPE_STOREONCE;
        }
        return ABGW_STORAGE_TYPE_CLOUD;
      }
      return null;
    },
    failedHosts: (state): ABGW_HostInfo[] => {
      if (state.config && Array.isArray(state.config.hosts)) {
        return state.config.hosts.filter(host => !host.systemd || host.systemd === SystemdStatus.FAILED);
      }
      return [];
    },
    getThresholdValue: state => (key: typeof SOFT_THRESHOLD | typeof S3_THRESHOLD) => state.throttlingLimits?.[key] || MIN_THROTTLING_LIMITS[key],
  },
  actions: {
    setConfig(config: ABGW_DeploymentInfo|null|false) {
      this.config = config;
    },
    async fetch() {
      try {
        this.loading = true;
        const clusterId = await getClusterId();
        const data = await api.get(`/api/v2/${clusterId}/abgw/deployment-info/`);
        this.config = data.data || false;
      } catch (err) {
        this.config = false;
      } finally {
        this.loading = false;
      }
    },
    async create(payload: DeployStandaloneABGW_RequestBody) {
      const clusterId = await getClusterId();
      return api.post<ScheduledTask>(`/api/v2/${clusterId}/abgw/deploy-standalone`, payload);
    },
    async destroy(nodes: Array<ID>) {
      const clusterId = await getClusterId();
      return api.post(`/api/v2/${clusterId}/abgw/nodes/leave`, { nodes })
        .then(waitTask)
        .then(() => {
          this.config = false;
        });
    },
    async getProgress() {
      const clusterId = await getClusterId();
      return api.get<ABGW_Process>(`/api/v2/${clusterId}/abgw/process`);
    },
    async processRetry(): Promise<FetchResponse<ScheduledTask>|void> {
      const { data } = await this.getProgress();
      if (!data.next) {
        return Promise.resolve();
      }
      const { href, method } = data.next;
      return api[method.toLowerCase()](href);
    },
    async assign(nodes: Array<ABGW_DeploymentNodes>) {
      const clusterId = await getClusterId();
      return api.post(`/api/v2/${clusterId}/abgw/nodes/join`, { nodes });
    },
    async release(nodes: Array<ID>) {
      const clusterId = await getClusterId();
      return api.post(`/api/v2/${clusterId}/abgw/nodes/leave`, { nodes });
    },
    async updateStorageParams(payload) {
      const clusterId = await getClusterId();
      await api.put(`/api/v2/${clusterId}/abgw/storage-params`, payload);
      this.config = { ...this.config, ...payload };
    },
    async verifyStorageParams(payload) {
      const clusterId = await getClusterId();
      return api.post(`/api/v2/${clusterId}/abgw/storage-params/verify`, payload);
    },
    async fetchVolumeParams() {
      this.loadingVolumeParams = true;
      try {
        const clusterId = await getClusterId();
        const { data } = await api.get<VolumeParamsWithReencodingV2>(`/api/v2/${clusterId}/abgw/volume-params`);
        this.volumeParams = data;
        clearTimeout(fetchTimerId);
        if (this.isVolumeParamsChanging) {
          fetchTimerId = setTimeout(() => { // TODO: Perhaps we can get rid of setTimout and use takeLast instead
            this.fetchVolumeParams();
          }, 1000);
        }
      } finally {
        this.loadingVolumeParams = false;
      }
    },
    async updateVolumeParams(payload: ABGW_VolumeParamsV2) {
      const clusterId = await getClusterId();
      return api.put(`/api/v2/${clusterId}/abgw/volume-params`, payload)
        .then(waitTask)
        .then(res => {
          this.fetchVolumeParams();
          return res;
        });
    },
    async retryProgressProcess(params): Promise<void> {
      const { failed, success, errorMessage } = params;
      this.processRetryInProgress = true;

      try {
        const progress = await this.processRetry();
        if (!progress) {
          return Promise.resolve();
        }
        const { data } = progress;
        const { task_id: operationId } = data;
        await store.dispatch('notifications/ADD', {
          type: 'info',
          message: i18n.t('Retrying operation on backup storage...'),
          operationId,
        });
        const { id } = await waitABGWTask({
          id: operationId,
          failed,
          success
        });
        await this.fetch();
        await store.dispatch('notifications/ADD', {
          type: 'success',
          message: i18n.t('Operation on backup storage successfully completed'),
          operationId: id
        });
        return Promise.resolve();
      } catch (e: any) {
        await store.dispatch('notifications/ADD', {
          type: 'error',
          message: errorMessage,
          operationId: e?.id,
          actions: [{
            label: i18n.t('Retry'),
            name: 'abgwRetryProgressProcess',
            payload: params
          }],
        });
        throw e;
      } finally {
        this.processRetryInProgress = false;
      }
    },
    async onTask(task: BackendTask) {
      if (task.name === TaskName.ABGW_STANDALONE_DEPLOYMENT && task.state === TaskState.SUCCESS) {
        await this.fetch();
      }
    },
    async getThrottlingLimits(): Promise<FetchResponse<ABGW_ThrottlingLimits>> {
      const clusterId = await getClusterId();
      return api.get(`/api/v2/${clusterId}/abgw/throttling-limits`).then(({ data }) => {
        this.throttlingLimits = data;
        return data;
      });
    },
    async setThrottlingLimits(payload: ABGW_ThrottlingLimits): Promise<FetchResponse<ScheduledTask>> {
      const clusterId = await getClusterId();
      return api.put(`/api/v2/${clusterId}/abgw/throttling-limits`, payload).then(({ data }) => {
        this.throttlingLimits = data;
        return data;
      });
    },
    onAbgw({ status }) {
      this.status = status;
      if (status === ABGW_DeploymentInfo.status.DEPLOYING_FAILED) {
        this.setConfig(false);
      }
    }
  },
  ws: {
    tasks: 'onTask',
    abgw: 'onAbgw',
  }
});

export default useAbgwStore;
